import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { XStack, Tooltip, StackProps, getTokenValue, Token } from 'tamagui'
import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'
import { Paragraph } from '../Text'

import { EventAttendeesOverlap_eventAttendees$key } from './__generated__/EventAttendeesOverlap_eventAttendees.graphql'

export const EventAttendeesOverlap = ({
  eventAttendees,
  size = '$6',
  limitAttendees,
  ...props
}: {
  eventAttendees: EventAttendeesOverlap_eventAttendees$key
  limitAttendees?: number
  size?: Token
} & StackProps) => {
  const data = useFragment(
    graphql`
      fragment EventAttendeesOverlap_eventAttendees on EventAttendeesConnection {
        edges {
          node {
            user {
              firstName
              lastName

              ...UserPrimaryPhotoNew_user @arguments(size: 48)
            }
          }

          cursor
        }
      }
    `,
    eventAttendees
  )

  const eventAttendeeSize = useMemo(() => {
    return getTokenValue(size, 'size')
  }, [size])

  const attendees = useMemo(() => {
    if (!limitAttendees) {
      return data.edges.toReversed()
    }

    return data.edges.slice(0, limitAttendees).toReversed()
  }, [data, limitAttendees])

  return (
    <XStack {...props} alignItems='center'>
      {attendees.map((edge, index) => {
        if (!edge.node.user) {
          return null
        }

        return (
          <Tooltip key={edge.cursor} placement='top' delay={100}>
            <Tooltip.Trigger
              marginLeft={index === 0 ? 0 : -(eventAttendeeSize / 3)}
            >
              <UserPrimaryPhoto size={size} user={edge.node.user} />
            </Tooltip.Trigger>

            <Tooltip.Content
              animation='fastSpring'
              enterStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
              exitStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
              scale={1}
              x={0}
              y={0}
              opacity={1}
              borderColor='rgba(105, 102, 118, 1.0)'
              backgroundColor='rgba(105, 102, 118, 0.8)'
              style={{
                backdropFilter: 'blur(8px) saturate(250%)',
                WebkitBackdropFilter: 'blur(8px) saturate(250%)',
              }}
            >
              <Tooltip.Arrow
                borderColor='rgba(105, 102, 118, 1.0)'
                backgroundColor='rgba(105, 102, 118, 0.7)'
                style={{
                  backdropFilter: 'blur(8px) saturate(250%)',
                  WebkitBackdropFilter: 'blur(8px) saturate(250%)',
                }}
              />

              <Paragraph size='$4' color='$grey0' fontWeight='$5'>
                {edge.node.user.firstName} {edge.node.user.lastName}
              </Paragraph>
            </Tooltip.Content>
          </Tooltip>
        )
      })}
    </XStack>
  )
}
